import { useState, useCallback } from "react";
import toast from "react-hot-toast";

import { Modal } from "shared/ui";
import { getFullEntityName } from "shared/lib/helpers";
import { CalendarDateI } from "shared/ui/calendar-form";
import { combineDateTimeWithZone } from "shared/lib/helpers/date";
import { useTimeout } from "shared/lib/hooks";
import { TIME_INTERVALS } from "shared/lib/constants/time";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";

import { useAccountsContext } from "@/hooks/use-accounts-context";
import { glencocoClientAPI, CalendarAttendeeI } from "@/api/glencoco";
import { MeetingTakerI } from "@/interfaces/accounts";
import { SCHEDULING_LINK_MODAL_ID } from "./constants";
import { dd } from "@/helpers/datadog";
import { ERROR_CATEGORIES } from "@/constants/errors";
import { dispatchCustomEvent } from "@/helpers/events";
import { CUSTOM_EVENTS } from "@/constants/custom-events";

import {
  VerifyingStep,
  ConfirmDetectedStep,
  FallbackStep,
  InstructionsStep,
} from "./components";
import { prefillSchedulingLink } from "shared/lib/helpers/scheduling";

const SCHEDULING_FORM_STEP_TYPES = {
  INSTRUCTIONS: "instructions",
  VERIFYING: "verifying",
  CONFIRM_DETECTED: "confirm_detected",
  FALLBACK: "fallback",
} as const;

type StepType = ValueOfObjectFields<typeof SCHEDULING_FORM_STEP_TYPES>;

interface DetectedMeetingI {
  meeting_time: string;
  event_id?: string;
  attendees?: CalendarAttendeeI[];
}

interface SchedulingLinkModalPropsI {
  modalId?: string;
  meetingTaker: MeetingTakerI;
  onSuccess: () => void;
  onClose?: () => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = TIME_INTERVALS.ONE_SECOND * 2;
const CONFIRM_BUTTON_DELAY_MS = TIME_INTERVALS.ONE_SECOND * 5;

const ModalContent = ({
  meetingTaker,
  onSuccess,
}: Pick<SchedulingLinkModalPropsI, "meetingTaker" | "onSuccess">) => {
  const { modalAccount } = useAccountsContext();

  // Step state
  const [step, setStep] = useState<StepType>(
    SCHEDULING_FORM_STEP_TYPES.INSTRUCTIONS
  );

  // Form state
  const [meetingTime, setMeetingTime] = useState<string>();
  const [detectedMeeting, setDetectedMeeting] =
    useState<DetectedMeetingI | null>(null);
  const [notes, setNotes] = useState<string>("");
  const [notesError, setNotesError] = useState(false);

  // UI state
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [showConfirmSection, setShowConfirmSection] = useState(false);

  // Show confirm button after delay to give user time to complete booking
  useTimeout(() => setShowConfirmSection(true), CONFIRM_BUTTON_DELAY_MS);

  // Derived values
  const schedulingLink = meetingTaker?.calendar_link;
  const meetingTakerName = getFullEntityName({
    first_name: meetingTaker?.first_name,
    last_name: meetingTaker?.last_name,
  });

  // Get prefilled scheduling link with prospect data
  const prefilledSchedulingLink = schedulingLink
    ? prefillSchedulingLink(schedulingLink, {
        name: modalAccount?.contact?.name,
        email: modalAccount?.contact?.email,
      })
    : null;

  // Handlers
  const handleOpenSchedulingLink = useCallback(() => {
    if (prefilledSchedulingLink) {
      const width = 800;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        prefilledSchedulingLink,
        "scheduling_link_popup",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      // If popup was blocked, open in new tab as fallback
      if (!popup || popup.closed || typeof popup.closed === "undefined") {
        window.open(prefilledSchedulingLink, "_blank");
      }
    }
  }, [prefilledSchedulingLink]);

  const handleCopyLink = useCallback(() => {
    if (prefilledSchedulingLink) {
      navigator.clipboard.writeText(prefilledSchedulingLink);
      toast.success("Link copied to clipboard");
    }
  }, [prefilledSchedulingLink]);

  const handleVerifyBooking = useCallback(async () => {
    if (!modalAccount?.contact?.email || !meetingTaker?.id) {
      toast.error("Missing contact email or meeting taker information");
      setStep(SCHEDULING_FORM_STEP_TYPES.FALLBACK);
      return;
    }

    setStep(SCHEDULING_FORM_STEP_TYPES.VERIFYING);

    const API = glencocoClientAPI();
    let found = false;
    let detected: DetectedMeetingI | null = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }

      try {
        const response = await API.detectMeetingFromCalendar(
          modalAccount.campaignId,
          modalAccount.accountId,
          {
            contact_email: modalAccount.contact.email,
            ae_id: meetingTaker.id,
          }
        );

        if (response.data.found && response.data.meeting_time) {
          found = true;
          detected = {
            meeting_time: response.data.meeting_time,
            event_id: response.data.event_id,
            attendees: response.data.attendees,
          };
          break;
        }
      } catch (error) {
        dd.rum.error(
          `${ERROR_CATEGORIES.BOOK_MEETING} - detect meeting attempt ${
            attempt + 1
          } failed`,
          { error }
        );
      }
    }

    if (found && detected) {
      setDetectedMeeting(detected);
      setStep(SCHEDULING_FORM_STEP_TYPES.CONFIRM_DETECTED);
    } else {
      setStep(SCHEDULING_FORM_STEP_TYPES.FALLBACK);
    }
  }, [modalAccount, meetingTaker]);

  const handleDateChange = useCallback(
    ({ date, time, timezone }: CalendarDateI) => {
      if (!time) {
        return;
      }

      setMeetingTime(
        combineDateTimeWithZone(date, time, timezone).toISOString()
      );
    },
    []
  );

  const handleNotesChange = useCallback((value: string) => {
    setNotes(value);

    if (value.trim()) {
      setNotesError(false);
    }
  }, []);

  const handleBookMeetingManually = useCallback(async () => {
    if (!meetingTime || !meetingTaker?.id) {
      toast.error("Please select a meeting time");
      return;
    }

    const contactId =
      modalAccount?.contact?.id || modalAccount?.contact?.contact_id;

    if (!contactId) {
      toast.error("Missing contact information");
      return;
    }

    setIsBookingLoading(true);

    const API = glencocoClientAPI();

    try {
      const response = await API.bookMeeting(
        modalAccount.campaignId,
        modalAccount.accountId,
        contactId,
        {
          account_executive_id: meetingTaker.id,
          meeting_time: meetingTime,
          notes,
        }
      );

      if (response.status === 200) {
        dispatchCustomEvent({
          eventType: CUSTOM_EVENTS.ACCOUNT.RELOAD,
        });
        dispatchCustomEvent({
          eventType: CUSTOM_EVENTS.ACCOUNT.HISTORY.RELOAD,
        });

        onSuccess();
      } else {
        toast.error("Failed to book meeting");
      }
    } catch (error) {
      toast.error("Failed to book meeting");
      dd.rum.error(`${ERROR_CATEGORIES.BOOK_MEETING} - manual booking failed`, {
        error,
      });
    } finally {
      setIsBookingLoading(false);
    }
  }, [meetingTime, notes, modalAccount, meetingTaker, onSuccess]);

  const handleConfirmDetectedMeeting = useCallback(async () => {
    if (!notes.trim()) {
      setNotesError(true);
      return;
    }

    if (!detectedMeeting?.meeting_time || !meetingTaker?.id) {
      toast.error("Missing meeting information");
      return;
    }

    const contactId =
      modalAccount?.contact?.id || modalAccount?.contact?.contact_id;

    if (!contactId) {
      toast.error("Missing contact information");
      return;
    }

    setIsBookingLoading(true);

    const API = glencocoClientAPI();

    try {
      const response = await API.bookMeeting(
        modalAccount.campaignId,
        modalAccount.accountId,
        contactId,
        {
          account_executive_id: meetingTaker.id,
          meeting_time: detectedMeeting.meeting_time,
          notes,
          calendar_event_id: detectedMeeting.event_id,
          attendees: detectedMeeting.attendees,
        }
      );

      if (response.status === 200) {
        dispatchCustomEvent({
          eventType: CUSTOM_EVENTS.ACCOUNT.RELOAD,
        });
        dispatchCustomEvent({
          eventType: CUSTOM_EVENTS.ACCOUNT.HISTORY.RELOAD,
        });

        onSuccess();
      } else {
        toast.error("Failed to book meeting");
      }
    } catch (error) {
      toast.error("Failed to book meeting");
      dd.rum.error(
        `${ERROR_CATEGORIES.BOOK_MEETING} - detected meeting booking failed`,
        { error }
      );
    } finally {
      setIsBookingLoading(false);
    }
  }, [detectedMeeting, notes, modalAccount, meetingTaker, onSuccess]);

  if (step === SCHEDULING_FORM_STEP_TYPES.VERIFYING) {
    return <VerifyingStep />;
  }

  if (step === SCHEDULING_FORM_STEP_TYPES.CONFIRM_DETECTED && detectedMeeting) {
    return (
      <ConfirmDetectedStep
        detectedMeeting={detectedMeeting}
        contactName={modalAccount?.contact?.name}
        accountName={modalAccount?.accountName}
        meetingTakerName={meetingTakerName}
        notes={notes}
        notesError={notesError}
        isLoading={isBookingLoading}
        onNotesChange={handleNotesChange}
        onConfirm={handleConfirmDetectedMeeting}
      />
    );
  }

  if (step === SCHEDULING_FORM_STEP_TYPES.FALLBACK) {
    return (
      <FallbackStep
        meetingTakerName={meetingTakerName}
        isLoading={isBookingLoading}
        onRetry={handleVerifyBooking}
        onDateChange={handleDateChange}
        onNotesChange={setNotes}
        handleOpenSchedulingLink={handleOpenSchedulingLink}
        onBookManually={handleBookMeetingManually}
      />
    );
  }

  return (
    <InstructionsStep
      meetingTakerName={meetingTakerName}
      contactName={modalAccount?.contact?.name}
      showConfirmSection={showConfirmSection}
      onOpenSchedulingLink={handleOpenSchedulingLink}
      onCopyLink={handleCopyLink}
      onVerifyBooking={handleVerifyBooking}
    />
  );
};

export const SchedulingLinkModal = ({
  modalId = SCHEDULING_LINK_MODAL_ID,
  meetingTaker,
  onSuccess,
  onClose,
}: SchedulingLinkModalPropsI) => (
  <Modal
    id={modalId}
    shouldCloseOnOverlayClick={false}
    showCloseButton
    modalBoxClassName="overflow-y-auto max-w-lg"
    title="Book meeting via scheduling link"
    titleClassName="typography-header-6 mb-6"
    unmountOnClose
    onClose={onClose}
  >
    <ModalContent meetingTaker={meetingTaker} onSuccess={onSuccess} />
  </Modal>
);

export { SCHEDULING_LINK_MODAL_ID };
